import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {RegionSimpleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {
  useDeleteRegionMutation,
  useGetRegionListV2Query,
} from 'src/redux/queries/region';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface RegionTableProps {
  /** Distinguishes persisted table state (column order/filters) across embeddings. */
  identifier?: string;
}

export const RegionTable: React.FC<RegionTableProps> = ({
  identifier = 'region',
}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [deleteTarget, setDeleteTarget] = useState<RegionSimpleResponse>();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteRegion] = useDeleteRegionMutation();

  const columns = useMemo<MRT_ColumnDef<RegionSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        Cell: ({cell}) => (
          <Text size="sm" lineClamp={2}>
            {cell.getValue<string | undefined>()}
          </Text>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetRegionListV2Query({
      start: start,
      length: length,
      filters: filters,
      filterFns: filterFns,
      sorting: sorting,
    });
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleDelete = async () => {
    if (!deleteTarget) return;

    confirmDeleteHandlers.close();
    setIsDeleting(true);

    try {
      await deleteRegion(deleteTarget.id).unwrap();
      setAlert(
        `${deleteTarget.name ?? 'Region'} was successfully deleted.`,
        'success'
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsDeleting(false);
    setDeleteTarget(undefined);
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <PermissionGuard
            module={ModuleEnum.Projects}
            operation={PermissionOperationEnum.Update}>
            <TableRowActions.Edit
              onClick={() =>
                navigate(PathUtility.ProjectRegionsEdit(row.original.id))
              }
            />
          </PermissionGuard>
          <PermissionGuard
            module={ModuleEnum.Projects}
            operation={PermissionOperationEnum.Delete}>
            <TableRowActions.Menu>
              <TableRowActions.MenuItem
                color="red"
                onClick={() => {
                  setDeleteTarget(row.original);
                  confirmDeleteHandlers.open();
                }}>
                Delete
              </TableRowActions.MenuItem>
            </TableRowActions.Menu>
          </PermissionGuard>
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <PermissionGuard
              module={ModuleEnum.Projects}
              operation={PermissionOperationEnum.Create}>
              <TableToolbar.Action
                variant="create"
                onClick={() => navigate(PathUtility.ProjectRegionsCreate())}>
                New Region
              </TableToolbar.Action>
            </PermissionGuard>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      {table}

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        confirmationButtonDisabled={isDeleting}
        title={`Are you sure you want to delete ${
          deleteTarget?.name ?? 'this region'
        }?`}
        body="Projects assigned to this region will no longer have one. This action cannot be undone."
      />
    </>
  );
};
